import type { IRewardsService } from '../../../services/rewards/IRewardsService';
import type { CatalogueSku } from '../../../services/rewards/types';

export type CatalogueState = {
  items: CatalogueSku[];
  loading: boolean;
  error: string | null;
};

export type CatalogueListener = (state: CatalogueState) => void;

export class CatalogueManager {
  private state: CatalogueState = { items: [], loading: false, error: null };
  private listeners: CatalogueListener[] = [];
  private destroyed = false;

  constructor(private readonly rewardsService: IRewardsService) {}

  getState(): CatalogueState {
    return this.state;
  }

  subscribe(listener: CatalogueListener): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  async fetchCatalogue(): Promise<void> {
    this.updateState({ loading: true });
    try {
      const data = await this.rewardsService.getCatalogue();
      if (this.destroyed) return;
      if (Array.isArray(data)) {
        const sorted = [...data].sort((a, b) => a.sortOrder - b.sortOrder);
        this.updateState({ items: sorted, error: null, loading: false });
      } else {
        this.updateState({ error: (data as any)?.error ?? 'Failed to load catalogue', loading: false });
      }
    } catch (err) {
      if (this.destroyed) return;
      this.updateState({
        error: err instanceof Error ? err.message : 'Failed to load catalogue',
        loading: false,
      });
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.listeners = [];
  }

  private updateState(partial: Partial<CatalogueState>): void {
    this.state = { ...this.state, ...partial };
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}
